<template>
  <!-- Explore by Itinerary section for homepage -->
  <div id="explore-itinerary-container">
    <!-- Loading indicator -->
    <a v-if="!loaded" name="explore-by-itinerary">
      <LoaderComponent />
    </a>
    <div v-else id="explore-itinerary-loader-wrapper">
      <div id="explore-itinerary">
        <div id="explore-itinerary-header">
          <a name="explore-by-itinerary">Explore by Itinerary</a>
        </div>
        <div
          id="explore-itinerary-description"
          v-html="exploreByItineraryDescription"
        ></div>
        <div id="explore-itinerary-dropdowns">
          <div id="itinerary-dropdowns-container">
            <!-- List of Countries which have related Itineraries -->
            <div
              id="itinerary-country-select-container"
              class="itinerary-dropdowns"
            >
              <label for="itinerary-country-select">Countries</label>
              <select
                id="itinerary-country-select"
                v-model="country"
                @change="goTo('itineraries', 'country', country)"
              >
                <option selected disabled value="">Select a Country</option>
                <option
                  v-for="country in countries"
                  :key="country.countryId"
                  :value="country.countryId"
                >
                  {{ country.countryName }}
                </option>
              </select>
            </div>
            <!-- List of Itineraries -->
            <div id="itinerary-select-container" class="itinerary-dropdowns">
              <label for="itinerary-select">Itineraries</label>
              <select
                id="itinerary-select"
                v-model="itinerary"
                @change="goTo('itineraries', 'itinerary', itinerary)"
              >
                <option selected disabled value="">Select an Itinerary</option>
                <option
                  v-for="itinerary in itineraries"
                  :key="itinerary.itineraryId"
                  :value="itinerary.itineraryId"
                >
                  {{ itinerary.fullName }}
                </option>
              </select>
            </div>
          </div>
        </div>
        <!-- Random/Highlighted Itineraries -->
        <div id="random-itineraries-container">
          <div id="random-itineraries-wrapper">
            <div
              id="random-itineraries-arrows-container"
              ref="itinerariesArrowsContainer"
              v-if="lessThan1199"
            >
              <button
                ref="itineraryLeftArrow"
                class="itinerary-arrow-button"
                @click="scrollRight()"
              >
                <font-awesome-icon
                  :icon="['fas', 'chevron-circle-left']"
                  class="itinerary-arrow"
                />
              </button>
              <button
                ref="itineraryRightArrow"
                class="itinerary-arrow-button"
                @click="scrollLeft()"
              >
                <font-awesome-icon
                  :icon="['fas', 'chevron-circle-right']"
                  class="itinerary-arrow"
                />
              </button>
            </div>
            <div id="random-itinerary-wrapper" ref="itineraryPanelContainer">
              <div
                class="random-itinerary"
                v-for="(itinerary, index) in random"
                :key="itinerary.itineraryId"
                :class="`itinerary-${index + 1}`"
                ref="itineraries"
              >
                <div
                  class="random-itinerary-image"
                  :style="{ backgroundImage: 'url(' + itinerary.image + ')' }"
                ></div>
                <div class="random-itinerary-overlay">
                  <div class="random-itinerary-info">
                    <span class="random-itinerary-country">{{
                      itinerary.countryName
                    }}</span>
                    <span class="random-itinerary-itinerary">{{
                      itinerary.itineraryName
                    }}</span>
                  </div>
                </div>
                <router-link
                  class="random-itinerary-link"
                  :to="{
                    name: 'itineraries',
                    params: {
                      country: `c-${itinerary.countryId}`,
                      itinerary: `i-${itinerary.itineraryId}`,
                    },
                  }"
                >
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoaderComponent from "./LoaderComponent.vue";
import language from "../mixins/language.js";
import media from "../mixins/media.js";
import homepageTexts from "../mixins/homepageTexts.js";
export default {
  name: "ExploreItinerary",
  components: {
    LoaderComponent: LoaderComponent,
  },
  mixins: [language, media, homepageTexts],
  data() {
    return {
      exploreByItineraryDescription: "",
      random: [],
      itineraries: [],
      itinerary: "",
      countries: [],
      country: "",
      loaded: false,

      lessThan1199: window.matchMedia("(max-width: 1199px)").matches,
      lessThan949: window.matchMedia("(max-width: 949px)").matches,
      lessThan599: window.matchMedia("(max-width: 599px)").matches,
    };
  },
  methods: {
    // Function for getting both Countries and Itineraries
    getData: function (url, category, data) {
      axios({ method: "GET", url: url }).then(
        (result) => {
          let results = result.data;
          let array = [];
          // Save relevant Country/Itinerary information
          for (let i = 0; i < results.length; i++) {
            let object = {};
            object[`${category}Id`] = results[i][`${category}Id`];
            object[`${category}Name`] = this.returnValueInLanguage(
              "en",
              results[i][`${category}Name`]
            ).value;
            object.link = results[i].links[category];
            if (category === "itinerary") {
              object.countryName = "";
              if (results[i].countryName.length > 0) {
                object.countryName = this.returnValueInLanguage(
                  "en",
                  results[i].countryName
                ).value;
              } else {
                object.countryName = "";
              }
              object.countryId = results[i].countryId;
              object.fullName = "";
              object.fullName = `${object.countryName} | ${object.itineraryName}`;
              object.image = "";
              object.image = results[i].itineraryImage;
              array.push(object);
            } else {
              array.push(object);
            }
          }
          // Sort differently for Itineraries v. Countries
          if (category === "itinerary") {
            array.sort(function (a, b) {
              let nameA = a.fullName;
              let nameB = b.fullName;
              return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
            });
          } else if (category === "country") {
            array.sort(function (a, b) {
              let nameA = a[`${category}Name`];
              let nameB = b[`${category}Name`];
              return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
            });
            this.loaded = true;
          }
          this[data] = array;
          if (category === "itinerary") {
            this.getRandomItineraries(this.itineraries);
          }
        },
        (error) => {
          console.error(error);
        }
      );
    },
    getRandomItinerary: function (array) {
      this.random = [];
      this.random = array[Math.floor(Math.random() * array.length)];
    },
    getRandomItineraries: function (array) {
      let itineraries = array.slice();
      this.random = [];
      let tmp,
        current,
        top = itineraries.length;
      if (top)
        while (--top) {
          current = Math.floor(Math.random() * (top + 1));
          tmp = itineraries[current];
          itineraries[current] = itineraries[top];
          itineraries[top] = tmp;
        }
      for (let i = 0; i < 4; i++) {
        this.random.push(itineraries[i]);
      }
    },
    goTo: function (path, param, id) {
      let prop = "";
      let object = {};
      if (param === "itinerary") {
        let countryId = "";
        prop = `i-${id}`;
        // Search Itineraries for selected ID to get Country ID
        for (let i = 0; i < this.itineraries.length; i++) {
          if (this.itineraries[i].itineraryId === id) {
            countryId = this.itineraries[i].countryId;
          }
        }
        // Add Country ID to router object
        object = { country: `c-${countryId}` };
      } else if (param === "country") {
        prop = `c-${id}`;
      }
      // Assign Itinerary/Country parameter accordingly
      object[param] = prop;
      this.$router.push({
        name: path,
        params: object,
      });
    },
    scrollLeft: function () {
      let panelWidth = this.$refs.itineraries[0].clientWidth + 20;
      let current = this.$refs.itineraryPanelContainer.scrollLeft;
      this.$refs.itineraryPanelContainer.scrollTo({
        left: current + panelWidth,
        behavior: "smooth",
      });
    },
    scrollRight: function () {
      let panelWidth = this.$refs.itineraries[0].clientWidth + 20;
      let current = this.$refs.itineraryPanelContainer.scrollLeft;
      this.$refs.itineraryPanelContainer.scrollTo({
        left: current - panelWidth,
        behavior: "smooth",
      });
    },
  },
  mounted() {
    this.getTexts("exploreByItineraryDescription", "Home-Explore-by-Itinerary");
    this.getData(
      `${process.env.VUE_APP_API_ENDPOINT}/itineraries/itineraries`,
      "itinerary",
      "itineraries"
    );
    this.getData(
      `${process.env.VUE_APP_API_ENDPOINT}/itineraries/countries`,
      "country",
      "countries"
    );
  },
};
</script>

<style scoped lang="scss">
.loader-container {
  height: 700px;
}

#explore-itinerary-container {
  width: 100%;
  padding: 0 40px;
  margin-top: 50px;
}

#explore-itinerary-header {
  @include headings;
  @include font-size-1;
  margin-bottom: 30px;
}
#explore-itinerary-header a {
  cursor: default;
}
#explore-itinerary-header a:hover {
  text-decoration: none;
}
#explore-itinerary-description {
  @include texts;
  @include font-size-4;
  color: $medium-dark-grey;
  margin-bottom: 20px;
}

#explore-itinerary-dropdowns {
  display: flex;
  width: 100%;
  box-shadow: 0px 2px 8px $light-grey;
  padding: 20px 30px;
  margin-bottom: 30px;
}
#itinerary-dropdowns-container {
  display: flex;
  justify-content: space-between;
  width: 100%;
}
.itinerary-dropdowns {
  display: flex;
  flex-direction: column;
  width: 50%;
  margin-left: 20px;
}
.itinerary-dropdowns:first-child {
  margin-left: 0;
}
.itinerary-dropdowns label {
  @include texts;
  @include font-size-4;
  color: $dark-blue;
  margin-bottom: 10px;
}
.itinerary-dropdowns select {
  height: 35px;
  width: 100%;
  padding-left: 15px;
}
#random-itineraries-container {
  display: flex;
  height: 600px;
  width: 100%;
}
#random-itineraries-wrapper {
  position: relative;
  width: 100%;
}
#random-itineraries-arrows-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  height: 600px;
  width: 100%;
}
.itinerary-arrow-button {
  @include button-styles;
  margin: 10px;
  z-index: 1;
}
.itinerary-arrow-button:focus {
  outline: none;
}
.itinerary-arrow {
  height: 50px;
  width: 50px;
  color: rgba(white, 0.6);
}
@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  #random-itineraries-arrows-container {
    display: none;
  }
}
#random-itinerary-wrapper {
  display: flex;
  position: relative;
  height: 100%;
  width: 100%;
  margin-bottom: 30px;
  overflow: auto;
  scrollbar-width: none; /* Firefox */
  // -ms-overflow-style: none; /* IE 10+ */
}
@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  #random-itinerary-wrapper {
    overflow-y: hidden;
  }
}
/* Chrome */
#random-itinerary-wrapper::-webkit-scrollbar {
  height: 0;
  width: 0;
}
.random-itinerary {
  height: 100%;
  min-width: calc(25% - 15px);
  width: calc(25% - 15px);
  position: relative;
  margin-left: 20px;
  overflow: hidden;
  z-index: 0;
}
.itinerary-1 {
  margin-left: 0;
}
.random-itinerary-image {
  height: 100%;
  width: 100%;
  background-color: $dark-green;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  transition: all 0.25s;
  z-index: -1;
}
.random-itinerary:hover .random-itinerary-image {
  transform: scale(1.2);
}
.random-itinerary-overlay {
  @include flex-center;
  height: 40%;
  width: 100%;
  position: absolute;
  bottom: 0;
  background-color: rgba($dark-grey, 0.5);
  padding: 20px 10px;
}
// .random-itinerary:hover .random-itinerary-overlay {
//   background-color: rgba($dark-grey, 0.8);
// }
.random-itinerary-info {
  @include flex-center;
  flex-direction: column;
  justify-content: start;
  height: 100%;
  width: 100%;
  text-align: center;
}
.random-itinerary-country {
  @include headings;
  @include font-size-3;
  color: white;
  height: 40%;
}
.random-itinerary-itinerary {
  @include headings;
  @include font-size-4;
  height: 60%;
  width: 100%;
  text-transform: none;
  color: white;
  text-align: center;
}
.random-itinerary-link {
  height: 100%;
  width: 100%;
  display: block;
  position: absolute;
  top: 0;
  left: 0;
}

@media only screen and (max-width: 1199px) {
  #explore-itinerary-header {
    @include font-size-2;
  }
  #explore-itinerary-description {
    @include font-size-5;
  }
  .random-itinerary {
    min-width: calc(33.3% - 15px);
    width: calc(33.3% - 15px);
  }
  .random-itinerary-country {
    @include font-size-4;
  }
  .random-itinerary-itinerary {
    font-weight: normal;
  }
}

@media only screen and (max-width: 949px) {
  #itinerary-dropdowns-container {
    flex-direction: column;
  }
  .itinerary-dropdowns {
    width: 100%;
    margin-left: 0;
    margin-top: 20px;
  }
  .itinerary-dropdowns:first-child {
    margin-top: 0;
  }
  .random-itinerary {
    min-width: calc(50% - 15px);
    width: calc(50% - 15px);
  }
}

@media only screen and (max-width: 599px) {
  #explore-itinerary-dropdowns {
    padding: 15px 20px;
  }
  #random-itineraries-container {
    height: 450px;
  }
  #random-itineraries-arrows-container {
    height: 450px;
  }
  .itinerary-arrow {
    height: 40px;
    width: 40px;
  }
  .random-itinerary {
    min-width: 100%;
    width: 100%;
  }
  .random-itinerary-itinerary {
    @include font-size-5;
    font-weight: bold;
  }
}

@media only screen and (max-width: 499px) {
  #explore-itinerary-header {
    @include font-size-4;
    margin-bottom: 20px;
  }
  #explore-itinerary-description {
    @include font-size-6;
  }
  .itinerary-dropdowns label {
    @include font-size-5;
  }
}
</style>
